"use client"

import { useState } from "react"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { X } from "lucide-react"

type ProjectCategory = "all" | "hdd" | "fiber" | "micro-trenching"

interface Project {
  id: number
  title: string
  category: Exclude<ProjectCategory, "all">
  description: string
  image: string
}

const projects: Project[] = [
  {
    id: 1,
    title: "Highway Crossing HDD",
    category: "hdd",
    description: "Major highway crossing using horizontal directional drilling for fiber conduit installation. Minimal surface disruption while connecting critical infrastructure.",
    image: "/images/gallery/hdd-highway.jpg",
  },
  {
    id: 2,
    title: "River Crossing Project",
    category: "hdd",
    description: "HDD river crossing maintaining environmental integrity. Zero impact to waterway while installing underground conduit.",
    image: "/images/gallery/hdd-river.jpg",
  },
  {
    id: 3,
    title: "Railroad HDD Crossing",
    category: "hdd",
    description: "Precision drilling beneath active railroad tracks. Coordinated with rail operators for safe installation.",
    image: "/images/gallery/hdd-railroad.jpg",
  },
  {
    id: 4,
    title: "Downtown Fiber Network",
    category: "fiber",
    description: "Complete fiber optic network installation in downtown business district. High-capacity backbone deployment.",
    image: "/images/gallery/fiber-downtown.jpg",
  },
  {
    id: 5,
    title: "Fiber Pulling Operation",
    category: "fiber",
    description: "Expert fiber optic cable pulling through existing conduit infrastructure. Multi-strand deployment for enterprise clients.",
    image: "/images/gallery/fiber-pulling.jpg",
  },
  {
    id: 6,
    title: "Urban Micro Trenching",
    category: "micro-trenching",
    description: "Precision micro trenching for rapid fiber deployment in urban environments with minimal restoration required.",
    image: "/images/gallery/micro-trenching.jpg",
  },
]

const categories: { value: ProjectCategory; label: string }[] = [
  { value: "all", label: "All Projects" },
  { value: "hdd", label: "HDD" },
  { value: "fiber", label: "Fiber Pulling" },
  { value: "micro-trenching", label: "Micro Trenching" },
]

export function GalleryGrid() {
  const [activeCategory, setActiveCategory] = useState<ProjectCategory>("all")
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)

  const filteredProjects = activeCategory === "all" 
    ? projects 
    : projects.filter(p => p.category === activeCategory)

  return (
    <section className="border-t border-border/40 bg-muted/30 py-20 md:py-28">
      <div className="container mx-auto px-4 md:px-6">
        {/* Filter Buttons */}
        <div className="mb-12 flex flex-wrap justify-center gap-2">
          {categories.map((category) => (
            <Button
              key={category.value}
              variant={activeCategory === category.value ? "default" : "outline"}
              className={activeCategory === category.value 
                ? "bg-primary text-primary-foreground" 
                : "border-border/50 bg-transparent hover:bg-primary/10"
              }
              onClick={() => setActiveCategory(category.value)}
            >
              {category.label}
            </Button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProjects.map((project) => (
            <button
              key={project.id}
              type="button"
              className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-border/50 bg-card text-left transition-all hover:shadow-xl"
              onClick={() => setSelectedProject(project)}
            >
              {/* Project Image */}
              <Image
                src={project.image || "/placeholder.svg"}
                alt={project.title}
                fill
                className="object-cover transition-transform duration-300 group-hover:scale-105"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
              
              {/* Content */}
              <div className="absolute inset-x-0 bottom-0 p-4 opacity-0 transition-all group-hover:opacity-100">
                <span className="mb-1 inline-block rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium text-primary">
                  {project.category.toUpperCase()}
                </span>
                <h3 className="text-lg font-semibold text-foreground">{project.title}</h3>
              </div>
            </button>
          ))}
        </div>

        {/* Empty State */}
        {filteredProjects.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-muted-foreground">No projects found in this category.</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <div className="relative max-h-[90vh] w-full max-w-3xl overflow-auto rounded-xl border border-border/50 bg-card">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 z-10"
              onClick={() => setSelectedProject(null)}
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </Button>
            
            <div className="relative aspect-video w-full">
              <Image
                src={selectedProject.image || "/placeholder.svg"}
                alt={selectedProject.title}
                fill
                className="object-cover"
              />
            </div>
            
            <div className="p-6">
              <span className="mb-2 inline-block rounded-full bg-primary/20 px-3 py-1 text-sm font-medium text-primary">
                {selectedProject.category.toUpperCase()}
              </span>
              <h2 className="mb-3 text-2xl font-bold text-foreground">{selectedProject.title}</h2>
              <p className="text-muted-foreground">{selectedProject.description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
